import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityRecord,
  IGetRecordResponse,
  IMigratedDataSummary,
  IPracticeMigration,
  ISourceEntityHandler,
  ISourceEntityRecord,
  ISourceEntityTypes,
  ITranslationMap,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ChartedSurface,
  ClinicalChart,
  ConditionConfigurationName,
  hasMergeConflicts,
} from '@principle-theorem/principle-core';
import {
  IChartedCondition,
  IChartedSurface,
  IClinicalChart,
  IPatient,
  IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  IIdentifiable,
  PrimaryKey,
  WithPrimaryKey,
  WithRef,
  resolveSequentially,
  getError,
  safeCombineLatest,
  toTimestamp,
  FirestoreMigrate,
} from '@principle-theorem/shared';
import { compact, uniqWith } from 'lodash';
import { Observable, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ItemCodeResourceMapType } from '../../mappings/item-codes-to-xlsx';
import { PraktikaPatient } from '../../praktika/source/entities/patient';
import { TranslationMapHandler } from '../../translation-map';
import { BaseDestinationEntity } from '../base-destination-entity';

export const PATIENT_CLINICAL_CHART_RESOURCE_TYPE = 'patientClinicalCharts';

export interface IBaseClinicalChartJobData<
  PatientRecord extends ISourceEntityTypes,
> {
  sourcePatient: IGetRecordResponse<PatientRecord>;
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  sourceItemCodes: WithPrimaryKey<
    ITranslationMap<object, ItemCodeResourceMapType>
  >[];
}

export interface IClinicalChartMigrationData {
  sourcePatientId: string;
  patientRef: DocumentReference<IPatient>;
  charts: IGeneratedCharts;
}

export interface IClinicalChartSuccessData {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  clinicalChartRefs: DocumentReference<IClinicalChart>[];
  perioChartRefs: DocumentReference<IClinicalChart>[];
}

export interface IGeneratedCharts {
  clinicalCharts: (IClinicalChart & IIdentifiable)[];
  perioCharts: (IClinicalChart & IIdentifiable)[];
}

export abstract class BasePatientClinicalChartDestinationEntity<
  PatientRecord extends ISourceEntityTypes,
  JobData extends IBaseClinicalChartJobData<PatientRecord>,
> extends BaseDestinationEntity<
  IClinicalChartSuccessData,
  JobData,
  IClinicalChartMigrationData
> {
  abstract override sourceCountComparison: ISourceEntityHandler<PatientRecord>;
  abstract periodontalResourceType: string;

  /**
   * Providers can chart missing teeth from more than one source (e.g. a missing
   * tooth list and tooth conditions), but each chart should hold a single
   * Missing Tooth condition that covers every missing tooth.
   */
  static mergeMissingToothConditions(
    conditions: IChartedCondition[]
  ): IChartedCondition[] {
    const missingToothConditions = conditions.filter(
      (condition) =>
        condition.config.name === ConditionConfigurationName.MissingTooth
    );
    const [firstCondition, ...duplicateConditions] = missingToothConditions;
    if (!firstCondition || !duplicateConditions.length) {
      return conditions;
    }

    const mergedCondition: IChartedCondition = {
      ...firstCondition,
      chartedSurfaces: uniqWith(
        missingToothConditions.flatMap(
          (condition) => condition.chartedSurfaces
        ),
        (surface, otherSurface) =>
          BasePatientClinicalChartDestinationEntity._isSameTooth(
            surface,
            otherSurface
          )
      ),
      notes: missingToothConditions.flatMap((condition) => condition.notes),
    };

    return conditions
      .filter((condition) => !duplicateConditions.includes(condition))
      .map((condition) =>
        condition === firstCondition ? mergedCondition : condition
      );
  }

  /**
   * A missing tooth has no surfaces, so refs on the same tooth describe the same
   * missing tooth whatever surface detail each source recorded.
   */
  private static _isSameTooth(
    surface: IChartedSurface,
    otherSurface: IChartedSurface
  ): boolean {
    const otherTooth = ChartedSurface.asWholeTooth(otherSurface);
    if (!otherTooth) {
      return ChartedSurface.isSameChartedRef(surface, otherSurface.chartedRef);
    }
    return ChartedSurface.isOnTooth(surface, otherTooth);
  }

  sourceCountDataAccessor(data: JobData): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  getDestinationEntityRecordUid(data: JobData): string {
    return data.sourcePatient.record.uid;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<IClinicalChartSuccessData>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return combineLatest([
      safeCombineLatest(
        record.data.clinicalChartRefs.map((clinicalChartRef) =>
          Firestore.getDoc(clinicalChartRef)
        )
      ),
      safeCombineLatest(
        record.data.perioChartRefs.map((perioChartRef) =>
          Firestore.getDoc(perioChartRef)
        )
      ),
    ]).pipe(
      map(([clinicalCharts, perioCharts]) => {
        const data: IMigratedDataSummary[] = [];
        data.push(
          ...clinicalCharts.map((clinicalChart) => ({
            label: 'Clinical Chart',
            data: clinicalChart,
          }))
        );
        data.push(
          ...perioCharts.map((perioChart) => ({
            label: 'Perio Chart',
            data: perioChart,
          }))
        );

        return data;
      })
    );
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    data: IClinicalChartMigrationData
  ): Promise<IClinicalChartMigrationData | undefined> {
    const existingCharts: (IClinicalChart & IIdentifiable)[] = [];

    const chartMergeConflicts = await resolveSequentially(
      compact(data.charts.clinicalCharts),
      async (chart) => {
        const chartRef = await translationMap.getDestination(
          data.sourcePatientId,
          this.destinationEntity.metadata.key
        );

        if (!chartRef) {
          return false;
        }

        const existingClinicalChart = await Firestore.getDoc(
          chartRef as DocumentReference<IClinicalChart>
        );

        existingCharts.push({
          ...existingClinicalChart,
          uid: data.sourcePatientId,
        });

        return hasMergeConflicts(chart, existingClinicalChart);
      }
    );

    const hasChartMergeConflicts = chartMergeConflicts.some(
      (chartMergeConflict) => chartMergeConflict
    );

    const existingPerioCharts: (IClinicalChart & IIdentifiable)[] = [];

    const perioChartMergeConflicts = await resolveSequentially(
      data.charts.perioCharts,
      async (perioChart) => {
        const perioChartRef = await translationMap.getDestination(
          perioChart.uid,
          this.periodontalResourceType
        );

        if (!perioChartRef) {
          return false;
        }

        const existingPerioChart = await Firestore.getDoc(
          perioChartRef as DocumentReference<IClinicalChart>
        );
        existingPerioCharts.push({
          ...existingPerioChart,
          uid: perioChart.uid,
        });

        return hasMergeConflicts(perioChart, existingPerioChart);
      }
    );

    const hasPerioMergeConflicts = perioChartMergeConflicts.some(
      (perioChartMergeConflict) => perioChartMergeConflict
    );

    if (hasChartMergeConflicts || hasPerioMergeConflicts) {
      return {
        ...data,
        charts: {
          ...data.charts,
          clinicalCharts: existingCharts,
          perioCharts: existingPerioCharts,
        },
      };
    }
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: JobData,
    _migrationData: IClinicalChartMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourcePatient.record.uid,
      label: jobData.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    jobData: JobData,
    migrationData: IClinicalChartMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const clinicalChartRefs = await this._upsertClinicalCharts(
        migrationData.charts.clinicalCharts,
        translationMapHandler,
        migrationData.patientRef
      );

      const perioChartRefs = await this._upsertPerioCharts(
        migrationData.charts.perioCharts,
        translationMapHandler,
        migrationData.patientRef
      );

      return this._buildSuccessResponse(
        jobData.sourcePatient,
        perioChartRefs,
        clinicalChartRefs
      );
    } catch (error) {
      return this._buildErrorResponse(
        jobData.sourcePatient.record,
        getError(error)
      );
    }
  }

  buildSkippedResponse(
    data: JobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  protected _buildSuccessResponse(
    patient: IGetRecordResponse<PatientRecord>,
    perioChartRefs: DocumentReference<IClinicalChart>[],
    clinicalChartRefs: DocumentReference<IClinicalChart>[]
  ): IDestinationEntityRecord<IClinicalChartSuccessData> {
    return {
      uid: patient.record.uid,
      label: patient.record.label,
      data: {
        sourceRef: patient.record.id,
        clinicalChartRefs,
        perioChartRefs,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: patient.record.id,
      migratedAt: toTimestamp(),
    };
  }

  protected _buildErrorResponse(
    patient: Pick<
      IGetRecordResponse<PraktikaPatient>['record'],
      'label' | 'uid' | 'id'
    >,
    errorMessage?: string
  ): IDestinationEntityRecord<IClinicalChartSuccessData> &
    FailedDestinationEntityRecord {
    return {
      uid: patient.uid,
      label: patient.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: patient.id,
      errorMessage: errorMessage ?? `Can't resolve data to generate charts`,
      failData: {
        patientRef: patient.id,
      },
    };
  }

  private async _upsertClinicalCharts(
    charts: (IClinicalChart & IIdentifiable)[],
    translationMap: TranslationMapHandler,
    patientRef: DocumentReference<IPatient>
  ): Promise<DocumentReference<IClinicalChart>[]> {
    return resolveSequentially(charts, async (chart) => {
      const chartDestinationRef = await translationMap.getDestination(
        chart.uid,
        this.destinationEntity.metadata.key
      );

      const chartRef = await FirestoreMigrate.upsertDoc(
        ClinicalChart.col({
          ref: patientRef,
        }),
        chart,
        chartDestinationRef?.id
      );

      if (!chartDestinationRef) {
        await translationMap.upsert({
          sourceIdentifier: chart.uid,
          destinationIdentifier: chartRef,
          resourceType: this.destinationEntity.metadata.key,
        });
      }

      return chartRef;
    });
  }

  private async _upsertPerioCharts(
    charts: (IClinicalChart & IIdentifiable)[],
    translationMap: TranslationMapHandler,
    patientRef: DocumentReference<IPatient>
  ): Promise<DocumentReference<IClinicalChart>[]> {
    return resolveSequentially(charts, async (chart) => {
      const chartDestinationRef = await translationMap.getDestination(
        chart.uid,
        this.periodontalResourceType
      );

      const chartRef = await FirestoreMigrate.upsertDoc(
        ClinicalChart.col({
          ref: patientRef,
        }),
        chart,
        chartDestinationRef?.id
      );

      if (!chartDestinationRef) {
        await translationMap.upsert({
          sourceIdentifier: chart.uid,
          destinationIdentifier: chartRef,
          resourceType: this.periodontalResourceType,
        });
      }

      return chartRef;
    });
  }
}
